import {
    Box,
    Paper,
    Table, TableBody,
    TableContainer, TableHead,
    TableRow, InputLabel, Typography, useTheme, useMediaQuery
} from '@mui/material';
import React, {Dispatch, useState} from 'react';
import {useAccelerator} from "../../hooks/acceleratorDataProvider";
import Button from "@mui/material/Button";
import {recordScore} from "../../interfaces/recordScore";
import dayjs from "dayjs";
import {axiosRequest} from "../../helpers/axios";
import ScoreCardDialog from "../dialogs/scoreCardDialog/scoreCardDialog";
import 'rsuite/dist/rsuite.min.css';
import subDays from 'date-fns/subDays';
import addDays from 'date-fns/addDays';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import addMonths from 'date-fns/addMonths';
import {DateRangePicker} from "rsuite";
import {RangeType} from "rsuite/esm/DateRangePicker/types";
import {DateRange} from "rsuite/cjs/DateRangePicker";
import DateIcon from "../icons/dateIcon";
import styles from './courseTable.module.css';
import PGEditIcon from "../icons/PGEdit";
import PGDeleteIcon from "../icons/PGDelete";
import {
    CourseTableCell,
    CourseTableHeaderCell,
    CourseTableRow,
    CourseTableSecondHeaderCell
} from "./courseTableStyling";
import {Accelerator} from "../../interfaces/accelerator";

const predefinedRanges: RangeType[] = [
    {
        label: 'Today',
        value: [new Date(), new Date()],
        placement: 'left'
    },
    {
        label: 'Yesterday',
        value: [addDays(new Date(), -1), addDays(new Date(), -1)],
        placement: 'left'
    },
    {
        label: 'Last 7 Days',
        value: [subDays(new Date(), 6), new Date()],
        placement: 'left'
    },
    {
        label: 'Last 30 Days',
        value: [subDays(new Date(), 29), new Date()],
        placement: 'left'
    },
    {
        label: 'This Month',
        value: [startOfMonth(new Date()), new Date()],
        placement: 'left'
    },
    {
        label: 'Last Month',
        value: [startOfMonth(addMonths(new Date(), -1)), endOfMonth(addMonths(new Date(), -1))],
        placement: 'left'
    },
    {
        label: 'Last 3 Months',
        value: [startOfMonth(addMonths(new Date(), -2)), endOfMonth(new Date())],
        placement: 'left'
    },
    {
        label: 'This Year',
        value: [new Date(new Date().getFullYear(), 0, 1), new Date(new Date().getFullYear(), 11, 31)],
        placement: 'left'
    },
    {
        label: 'Last Year',
        value: [new Date(new Date().getFullYear() - 1, 0, 1), new Date(new Date().getFullYear(), 0, 0)],
        placement: 'left'
    },
    {
        label: 'All Time',
        value: [new Date(new Date().getFullYear() - 10, 0, 1), new Date()],
        placement: 'left'
    }
];

const getFormattedDate = (date: Date) => {
    return date.toLocaleString('en-us', {month: 'long', year: 'numeric', day: 'numeric'});
}

const getDateRangeRenderValue = (value: DateRange) => {
    if (getFormattedDate(value[0]) === getFormattedDate(addDays(new Date(), -1)) && getFormattedDate(value[1]) === getFormattedDate(addDays(new Date(), -1)))
        return "Yesterday";
    else if (getFormattedDate(value[0]) === getFormattedDate(new Date()) && getFormattedDate(value[1]) === getFormattedDate(new Date()))
        return "Today";
    else if (getFormattedDate(value[0]) === getFormattedDate(subDays(new Date(), 6)) && getFormattedDate(value[1]) === getFormattedDate(new Date()))
        return "Last 7 Days";
    else if (getFormattedDate(value[0]) === getFormattedDate(subDays(new Date(), 29)) && getFormattedDate(value[1]) === getFormattedDate(new Date()))
        return "Last 30 Days";
    else if (getFormattedDate(value[0]) === getFormattedDate(new Date(new Date().getFullYear(), 0, 1)) && getFormattedDate(value[1]) === getFormattedDate(new Date(new Date().getFullYear(), 11, 31)))
        return "This Year";
    else if (getFormattedDate(value[0]) === getFormattedDate(startOfMonth(new Date())) && getFormattedDate(value[1]) === getFormattedDate(new Date()))
        return "This Month";
    else if (getFormattedDate(value[0]) === getFormattedDate(startOfMonth(addMonths(new Date(), -1))) && getFormattedDate(value[1]) === getFormattedDate(endOfMonth(addMonths(new Date(), -1))))
        return "Last Month";
    else if (getFormattedDate(value[0]) === getFormattedDate(startOfMonth(addMonths(new Date(), -2))) && getFormattedDate(value[1]) === getFormattedDate(endOfMonth(new Date())))
        return "Last 3 Months";
    else if (getFormattedDate(value[0]) === getFormattedDate(new Date(new Date().getFullYear() - 1, 0, 1)) && getFormattedDate(value[1]) === getFormattedDate(new Date(new Date().getFullYear(), 0, 0)))
        return "Last Year";
    else if (getFormattedDate(value[0]) === getFormattedDate(new Date(new Date().getFullYear() - 10, 0, 1)) && getFormattedDate(value[1]) === getFormattedDate(new Date()))
        return "All Time";
    else
        return value[0].toLocaleString('en-us', {
            month: 'long',
            year: 'numeric',
            day: 'numeric'
        }) + " - " + value[1].toLocaleString('en-us', {month: 'long', year: 'numeric', day: 'numeric'});
}

const CourseTable = ({records, recordDispatcher, date, setDate}: {
    records: recordScore[],
    recordDispatcher: Dispatch<{ type: string, payload: any }>,
    date: [Date, Date],
    setDate: (Array: [Date, Date]) => void
}) => {
    const {accelerators, setAverageScore} = useAccelerator();
    const [showScoreBoardDialog, setShowScoreBoardDialog] = useState(false);
    const [currentRecord, setCurrentRecord] = useState<recordScore>({
        ball_striking: "",
        chipping_bunkers_1: "",
        chipping_bunkers_2: "",
        course: "",
        driving: "",
        mental_game: "",
        putting: "",
        record_date: "",
        record_id: 0,
        score: 0,
        wedge_game_1: "",
        wedge_game_2: "",
    });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const [showAddScoreBoardDialog, setShowAddScoreBoardDialog] = useState(false);

    const deleteRecord = (record_id: number) => {
        axiosRequest("DELETE", "/wp-json/pg/v2/sc/score-tracker/" + record_id)
            .then(response => {
                recordDispatcher({type: "remove", payload: record_id})
                setAverageScore(response.data.data.avg)
            })
    }

    const editRecord = (record: recordScore) => {
        setCurrentRecord(record);
        setShowScoreBoardDialog(true);
    }

    const getAverageColumnValue = (index: number) => {
        switch (index) {
            case 0:
                return records.reduce((total, next) => {
                    return total + (Number(next.chipping_bunkers_2) ? (Number(next.chipping_bunkers_1) / Number(next.chipping_bunkers_2)) * 100 : 0)
                }, 0) / records.length
            case 1:
                return records.reduce((total, next) => total + Number(next.putting), 0) / records.length
            case 2:
                return records.reduce((total, next) => total + Number(next.mental_game), 0) / records.length
            case 3:
                return records.reduce((total, next) => {
                    return total + (Number(next.wedge_game_2) ? (Number(next.wedge_game_1) / Number(next.wedge_game_2)) : 0)
                }, 0) / records.length
            case 4:
                return records.reduce((total, next) => total + Number(next.driving), 0) / records.length
            case 5:
                return records.reduce((total, next) => total + Number(next.ball_striking), 0) / records.length
            default:
                return 0;
        }
    }

    const getAverageScore = () => {
        return records.length ? (records.reduce((total, next) => total + next.score, 0) / records.length).toFixed(1).replace('.0', '') : "0"
    }

    const addRecordToTable = (response: any, scoreboard: recordScore) => {
        let scoreDate = new Date(scoreboard.record_date.toString());
        const start = new Date(date[0]);
        const end = new Date(date[1]);
        if (scoreDate < start || scoreDate > end) {
            setDate([new Date(new Date().getFullYear() - 10, 0, 1), new Date()]);
        } else {
            recordDispatcher({
                type: "add",
                payload: {...scoreboard, record_id: response.record_id}
            })
        }
        setAverageScore(response.avg)
    }

    const editTableRecord = (response: any, scoreboard: recordScore) => {
        recordDispatcher({type: "edit", payload: scoreboard});
        setAverageScore(response.avg)
    }

    const getAcceleratorDescription = (accelerator: Accelerator) => {
        switch (accelerator.name) {
            case "Chipping & Bunkers":
                return "Up & Down %";
            case "Putting":
                return "Putts";
            case "Mental Game":
                return "Mental Score";
            case "Wedge Game":
                return "Strokes from 30-100";
            case "Driving":
                return "Fairway Hit";
            case "Ball Striking":
                return "Green in Regulation";
            default:
                return 0;
        }
    }

    return (
        <>
            <Box sx={{marginTop: {xs: "20px", md: "30px"}, display: "flex"}}>
                <Box>
                    <Typography sx={{fontSize: 12, textTransform: isMobile ? "none" : "uppercase", fontWeight: 400, fontFamily: isMobile ? "'Lato', sans-serif" : "'Repro Mono', sans-serif"}}>Date Range</Typography>
                    <InputLabel id="demo-simple-select-label"></InputLabel>
                    <DateRangePicker
                        value={date}
                        onChange={(value) => {
                            if (value)
                                setDate([value[0], value[1]])
                        }}
                        className={styles.badge}
                        cleanable={false}
                        format={"MM/dd/yyyy"}
                        caretAs={DateIcon}
                        ranges={predefinedRanges}
                        renderValue={(value, format) => {
                            return getDateRangeRenderValue(value)
                        }}
                        placeholder="Date range"
                        style={{width: isMobile ? 220 : 300, fontFamily: "'Repro', sans-serif", fontSize: 16, fontWeight: 400, height: "48px"}}
                    />
                </Box>
                <Box
                    onClick={() => window.open("/contact-us/")}
                    sx={{display: "flex", alignItems: "flex-end", margin: "0 0 0 auto", color: "#1D1A1A", cursor: "pointer"}}>
                    <Typography sx={{marginRight: "5px", color: "#7B726C", fontSize: 16, fontWeight: 500, marginTop: isMobile ? "18px" : "0"}}>Need help?</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", width: "24px", marginRight: "22px", marginTop: isMobile ? "18px" : "0"}}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z" stroke="#7B726C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M9.27727 7.795C9.43594 7.655 9.60861 7.522 9.79527 7.396C9.98661 7.27 10.1919 7.16033 10.4113 7.067C10.6306 6.97367 10.8686 6.90133 11.1253 6.85C11.3819 6.794 11.6596 6.766 11.9583 6.766C12.3643 6.766 12.7329 6.822 13.0643 6.934C13.4003 7.046 13.6873 7.207 13.9253 7.417C14.1633 7.62233 14.3476 7.872 14.4783 8.166C14.6089 8.46 14.6743 8.789 14.6743 9.153C14.6743 9.50767 14.6229 9.81567 14.5203 10.077C14.4176 10.3337 14.2893 10.5577 14.1353 10.749C13.9813 10.9403 13.8109 11.106 13.6243 11.246C13.4423 11.386 13.2696 11.519 13.1063 11.645C12.9429 11.7663 12.8006 11.8877 12.6793 12.009C12.5626 12.1257 12.4926 12.2587 12.4693 12.408L12.3083 13.43H11.1253L11.0063 12.289C10.9783 12.0697 11.0086 11.8783 11.0973 11.715C11.1906 11.547 11.3119 11.3953 11.4613 11.26C11.6153 11.12 11.7856 10.987 11.9723 10.861C12.1589 10.7303 12.3339 10.5903 12.4973 10.441C12.6606 10.287 12.7959 10.1167 12.9033 9.93C13.0153 9.73867 13.0713 9.51233 13.0713 9.251C13.0713 9.083 13.0386 8.93367 12.9733 8.803C12.9126 8.66767 12.8263 8.55333 12.7143 8.46C12.6023 8.362 12.4669 8.28733 12.3083 8.236C12.1543 8.18467 11.9863 8.159 11.8043 8.159C11.5383 8.159 11.3119 8.18933 11.1253 8.25C10.9433 8.306 10.7869 8.37133 10.6563 8.446C10.5303 8.516 10.4229 8.58133 10.3343 8.642C10.2456 8.698 10.1663 8.726 10.0963 8.726C9.92827 8.726 9.80694 8.656 9.73227 8.516L9.27727 7.795ZM10.6003 16.048C10.6003 15.9033 10.6259 15.7657 10.6773 15.635C10.7333 15.5043 10.8079 15.3923 10.9013 15.299C10.9946 15.2057 11.1066 15.131 11.2373 15.075C11.3679 15.019 11.5079 14.991 11.6573 14.991C11.8066 14.991 11.9443 15.019 12.0703 15.075C12.2009 15.131 12.3129 15.2057 12.4063 15.299C12.5043 15.3923 12.5813 15.5043 12.6373 15.635C12.6933 15.7657 12.7213 15.9033 12.7213 16.048C12.7213 16.1973 12.6933 16.3373 12.6373 16.468C12.5813 16.594 12.5043 16.7037 12.4063 16.797C12.3129 16.8903 12.2009 16.9627 12.0703 17.014C11.9443 17.07 11.8066 17.098 11.6573 17.098C11.5079 17.098 11.3679 17.07 11.2373 17.014C11.1066 16.9627 10.9946 16.8903 10.9013 16.797C10.8079 16.7037 10.7333 16.594 10.6773 16.468C10.6259 16.3373 10.6003 16.1973 10.6003 16.048Z" fill="#7B726C"/>
                        </svg>
                    </Box>
                </Box>
            </Box>

            <TableContainer component={Paper} sx={{boxShadow: "none", marginBottom: {xs: "0", md: "40px"}}}>
                <Table sx={{minWidth: 300, marginTop: "20px"}} aria-label="customized table">
                    <TableHead>
                        <TableRow>
                            <CourseTableHeaderCell sx={{color: isMobile ? '#7B726C' : '#182026'}}>Date</CourseTableHeaderCell>
                            <CourseTableHeaderCell sx={{minWidth: '170px', color: isMobile ? '#7B726C' : '#182026'}}>Course</CourseTableHeaderCell>
                            <CourseTableHeaderCell sx={{color: isMobile ? '#7B726C' : '#182026'}}>Score</CourseTableHeaderCell>
                            {
                                accelerators.map((accelerator) =>
                                    <CourseTableHeaderCell key={accelerator.ID} sx={{color: isMobile ? '#7B726C' : '#182026'}}>
                                        <Typography sx={{
                                            fontSize: 16,
                                            fontWeight: 500,
                                            lineHeight: "18px",
                                            marginBottom: "5px",
                                            color: isMobile ? '#7B726C' : '#182026',
                                            fontFamily: isMobile ? "'Lato', sans-serif" : "'Repro', sans-serif",
                                        }}>{accelerator.name}</Typography>
                                        <Typography sx={{fontSize: 12, fontWeight: 400, fontFamily: isMobile ? "'Lato', sans-serif" : "'Repro', sans-serif"}}>
                                            {getAcceleratorDescription(accelerator)}</Typography>
                                    </CourseTableHeaderCell>
                                )
                            }
                            {<>
                                <CourseTableHeaderCell></CourseTableHeaderCell>
                            </>}
                        </TableRow>
                        <TableRow sx={{backgroundColor: "#F4F2F0"}}>
                            <CourseTableSecondHeaderCell sx={{color: isMobile ? '#7B726C' : '#182026'}}>Average</CourseTableSecondHeaderCell>
                            <CourseTableSecondHeaderCell sx={{color: isMobile ? '#7B726C' : '#182026'}}></CourseTableSecondHeaderCell>
                            <CourseTableSecondHeaderCell sx={{color: isMobile ? '#7B726C' : '#182026'}}>
                                <Box sx={{
                                    display: "flex",
                                    justifyContent: "left",
                                    alignItems: "left",
                                }}>
                                    <Typography
                                        sx={{
                                            display: "inline-block",
                                            padding: getAverageScore().includes(".") ? getAverageScore().length > 3 ? "8px 5px" : "5px 5px" : getAverageScore().length > 1 ? "2px 5px" : "1px 8px",
                                            borderRadius: "50%",
                                            textAlign: "center",
                                            background: "#FD3300",
                                            color: "#ffffff",
                                            fontSize: "16px",
                                            fontWeight: 700
                                        }}
                                    >{getAverageScore()}</Typography>
                                </Box>

                            </CourseTableSecondHeaderCell>
                            {
                                accelerators.map((accelerator, index) =>
                                    <CourseTableSecondHeaderCell key={accelerator.ID} sx={{color: isMobile ? '#7B726C' : '#182026'}}>
                                        {!isNaN(getAverageColumnValue(index)) ? getAverageColumnValue(index).toFixed(1).replace('.0', '') : 0}</CourseTableSecondHeaderCell>
                                )
                            }
                            {<>
                                <CourseTableSecondHeaderCell></CourseTableSecondHeaderCell>
                            </>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {records.map((record, index) => (
                            <CourseTableRow key={index}>
                                <CourseTableCell component="th" scope="row" sx={{color: isMobile ? '#7B726C' : '#182026'}}>
                                    {dayjs(record.record_date, 'YYYY-MM-DD').format("MM/DD/YYYY")}
                                </CourseTableCell>
                                <CourseTableCell sx={{minWidth: '150px', color: isMobile ? '#7B726C' : '#182026'}}>{record.course}</CourseTableCell>
                                <CourseTableCell sx={{color: isMobile ? '#7B726C' : '#182026'}}>{record.score}</CourseTableCell>
                                {<>
                                    <CourseTableCell sx={{color: isMobile ? '#7B726C' : '#182026'}}>{
                                        Number(record.chipping_bunkers_2) > 0 ?
                                            ((Number(record.chipping_bunkers_1) / Number(record.chipping_bunkers_2)) * 100).toFixed(1) : 0
                                    }</CourseTableCell>
                                    <CourseTableCell sx={{color: isMobile ? '#7B726C' : '#182026'}}>{record.putting}</CourseTableCell>
                                    <CourseTableCell sx={{color: isMobile ? '#7B726C' : '#182026'}}>{record.mental_game ? record.mental_game : 0}</CourseTableCell>
                                    <CourseTableCell sx={{color: isMobile ? '#7B726C' : '#182026'}}>{
                                        Number(record.wedge_game_2) > 0 ?
                                            (Number(record.wedge_game_1) / Number(record.wedge_game_2)).toFixed(1) : 0
                                    }</CourseTableCell>
                                    <CourseTableCell sx={{color: isMobile ? '#7B726C' : '#182026'}}>{record.driving}</CourseTableCell>
                                    <CourseTableCell sx={{color: isMobile ? '#7B726C' : '#182026'}}>{record.ball_striking}</CourseTableCell>
                                    <CourseTableCell>
                                        <Box sx={{display: "flex"}}>
                                            <Box sx={{
                                                marginLeft: "auto",
                                                display: "flex",
                                                justifyContent: "flex-end"
                                            }}>
                                                <Button variant="text"
                                                        sx={{fontFamily: "'Repro', sans-serif", fontWeight: "400 !important"}}
                                                        onClick={() => editRecord(record)}
                                                        className={styles.tableActions}
                                                        disableRipple startIcon={<PGEditIcon/>}>
                                                    Edit
                                                </Button>
                                                <Button variant="text"
                                                        sx={{marginLeft: "15px", fontFamily: "'Repro', sans-serif", fontWeight: "400 !important"}}
                                                        onClick={() => deleteRecord(record.record_id as number)}
                                                        className={styles.tableActions}
                                                        disableRipple startIcon={<PGDeleteIcon/>}>
                                                    Delete
                                                </Button>
                                            </Box>
                                        </Box>
                                    </CourseTableCell>
                                </>}
                            </CourseTableRow>
                        ))}
                    </TableBody>
                </Table>


            </TableContainer>

            {/*<Button variant="contained"
                    hidden={!isMobile}
                    onClick={() => setShowAddScoreBoardDialog(true)}
                    fullWidth={true}
                    sx={{
                        fontSize: "16px",
                        lineHeight: "20px",
                        fontWeight: 700,
                        backgroundColor: "#FD3300",
                        padding: "10px",
                        marginTop: {xs: "20px", md: "auto"},
                        fontFamily: "'Repro', sans-serif",
                        marginBottom: "40px",
                        textTransform: "capitalize",
                        borderRadius: "100px",
                        "&:hover": {
                            backgroundColor: "#E02B00"
                        }
                    }}
            >
                Submit scoreboard
            </Button>*/}

            <ScoreCardDialog show={showAddScoreBoardDialog} setShow={setShowAddScoreBoardDialog}
                             callback={addRecordToTable}/>

            <ScoreCardDialog show={showScoreBoardDialog} setShow={setShowScoreBoardDialog}
                             callback={editTableRecord} record={currentRecord}/>
        </>
    );
};


export default CourseTable;